import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { log } from "../../logger";
import type { ScanFormat, ScanPayload } from "../scanTypes";

/** Everything a scan can resolve to, as a display snapshot (ADR-0041). */
export type ScanOutcomeKind =
  | "container"
  | "item"
  | "created-item"
  | "payload"
  | "none";

/**
 * The display snapshot written when a scan resolves. It is a fallback label
 * only: opening a history entry re-resolves its target live (ADR-0041).
 */
export interface ScanOutcome {
  kind: ScanOutcomeKind;
  /** The container/item id, when the outcome names a target. */
  id?: string;
  /** The target's name at resolution time, for previews. */
  label?: string;
}

/** One recalled scan: the decoded code, its payload and the outcome snapshot. */
export interface ScanHistoryEntry {
  /** Stable entry identity, also the `updateOutcome` key. */
  id: string;
  /** The scan code: the canonical label UUID, or the trimmed raw payload. */
  code: string;
  /** Epoch milliseconds of the decode (refreshed on a collapsed repeat). */
  at: number;
  /** The symbology that produced the code (`manual` for typed entry). */
  format: ScanFormat;
  /** The classified payload, with its raw text capped at 1024 characters. */
  payload: ScanPayload;
  /** How often this code was scanned inside the dedupe window. */
  count: number;
  /** Written when resolution lands; absent for entries that never resolved. */
  outcome?: ScanOutcome;
}

/** What a recorded decode needs: the code, its symbology and its payload. */
export interface RecordScanInput {
  code: string;
  format: ScanFormat;
  payload: ScanPayload;
}

interface ScanHistoryState {
  /** The persisted slice: one newest-first bucket per signed-in username. */
  historyByUser: Record<string, ScanHistoryEntry[]>;
  /** The signed-in identity whose bucket reads and writes target. */
  activeUser?: string;
  /** Point the store at a user's bucket (identity sync from the app store). */
  setActiveUser: (username?: string) => void;
  /**
   * Record a decoded or typed scan for the active user. A repeat of the
   * newest entry's code within {@link SCAN_HISTORY_DEDUPE_MS} refreshes that
   * entry instead of appending. Returns the entry id, or undefined when
   * signed out or the code is empty.
   */
  recordScan: (input: RecordScanInput) => string | undefined;
  /** Write the display snapshot once an entry's target has resolved. */
  updateOutcome: (entryId: string, outcome: ScanOutcome) => void;
  /** Drop the active user's history (the overlay's "clear history"). */
  clearHistory: () => void;
  /** Drop one user's bucket (logout); other users keep theirs. */
  clearForUser: (username?: string) => void;
  /** The active user's entries, newest first; `username` overrides the bucket. */
  entries: (username?: string) => readonly ScanHistoryEntry[];
}

export const SCAN_HISTORY_STORAGE_KEY = "egalregal-scan-history";
export const SCAN_HISTORY_STORAGE_VERSION = 1;
/** The feature's recall depth: anything older is dropped (ADR-0041). */
export const SCAN_HISTORY_LIMIT = 30;
/** A repeat of the newest code inside this window collapses into its entry. */
export const SCAN_HISTORY_DEDUPE_MS = 5_000;

/** Stable empty list so selectors do not churn on every render. */
export const EMPTY_SCAN_HISTORY: readonly ScanHistoryEntry[] = Object.freeze([]);

const VALID_FORMATS: ReadonlySet<string> = new Set<ScanFormat>([
  "qr",
  "ean_13",
  "ean_8",
  "upc_a",
  "upc_e",
  "code_128",
  "code_39",
  "manual",
  "unknown",
]);

const VALID_OUTCOMES: ReadonlySet<string> = new Set<ScanOutcomeKind>([
  "container",
  "item",
  "created-item",
  "payload",
  "none",
]);

const VALID_PAYLOAD_KINDS: ReadonlySet<string> = new Set<ScanPayload["kind"]>([
  "label",
  "product",
  "url",
  "wifi",
  "text",
]);

let fallbackIdCounter = 0;

/** A collision-resistant entry id without depending on a secure context. */
function newEntryId(): string {
  const uuid = globalThis.crypto?.randomUUID?.();
  if (uuid) return uuid;
  fallbackIdCounter += 1;
  return `${Date.now().toString(36)}-${fallbackIdCounter}`;
}

/**
 * Storage wrapper with the preferences store's corrupt-state discipline
 * (ADR-0041): invalid JSON or a wrong-shaped state drops the key and falls
 * back to defaults; a full quota keeps the history in memory.
 */
function safeStorage() {
  return {
    getItem: (key: string): string | null => {
      try {
        const raw = localStorage.getItem(key);
        if (!raw) return null;
        const parsed: unknown = JSON.parse(raw);
        const state = (parsed as { state?: unknown })?.state;
        if (typeof state !== "object" || state === null || Array.isArray(state)) {
          log.warn({ key }, "[scan-history] Ignoring wrong-shaped stored state; using defaults");
          localStorage.removeItem(key);
          return null;
        }
        return raw;
      } catch (err) {
        log.warn({ err, key }, "[scan-history] Ignoring corrupt stored state; using defaults");
        try {
          localStorage.removeItem(key);
        } catch (cleanupErr) {
          log.debug({ err: cleanupErr, key }, "[scan-history] Could not clear corrupt stored state");
        }
        return null;
      }
    },
    setItem: (key: string, value: string): void => {
      try {
        localStorage.setItem(key, value);
      } catch (err) {
        // Storage full or unavailable: the history stays in memory. This is a
        // silent data-loss path for the user, so it is worth a warning.
        log.warn({ err, key }, "[scan-history] Could not persist history; keeping in-memory state");
      }
    },
    removeItem: (key: string): void => {
      try {
        localStorage.removeItem(key);
      } catch (err) {
        log.debug({ err, key }, "[scan-history] Could not clear stored history");
      }
    },
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isOptionalString(value: unknown): boolean {
  return value === undefined || typeof value === "string";
}

/** Validate a classified payload before it may be rendered or re-persisted. */
function isValidPayload(value: unknown): value is ScanPayload {
  if (!isRecord(value)) return false;
  if (typeof value.raw !== "string" || typeof value.truncated !== "boolean") return false;
  if (typeof value.kind !== "string" || !VALID_PAYLOAD_KINDS.has(value.kind)) return false;
  switch (value.kind) {
    case "label":
      return typeof value.uuid === "string";
    case "product":
      return typeof value.code === "string";
    case "url":
      return typeof value.href === "string";
    case "wifi":
      return (
        typeof value.ssid === "string" &&
        typeof value.hidden === "boolean" &&
        isOptionalString(value.security) &&
        isOptionalString(value.password)
      );
    case "text":
      return typeof value.text === "string";
    default:
      return false;
  }
}

function isValidOutcome(value: unknown): value is ScanOutcome {
  if (!isRecord(value)) return false;
  if (typeof value.kind !== "string" || !VALID_OUTCOMES.has(value.kind)) return false;
  return isOptionalString(value.id) && isOptionalString(value.label);
}

function isValidEntry(value: unknown): value is ScanHistoryEntry {
  if (!isRecord(value)) return false;
  return (
    typeof value.id === "string" &&
    value.id !== "" &&
    typeof value.code === "string" &&
    value.code !== "" &&
    typeof value.at === "number" &&
    Number.isFinite(value.at) &&
    typeof value.format === "string" &&
    VALID_FORMATS.has(value.format) &&
    isValidPayload(value.payload) &&
    typeof value.count === "number" &&
    Number.isInteger(value.count) &&
    value.count >= 1 &&
    (value.outcome === undefined || isValidOutcome(value.outcome))
  );
}

/**
 * Sanitize one restored bucket: drop malformed entries and apply the cap, so a
 * hand-edited or older payload can never render or grow past the contract.
 */
function sanitizeBucket(value: unknown): ScanHistoryEntry[] {
  if (!Array.isArray(value)) return [];
  return value.filter(isValidEntry).slice(0, SCAN_HISTORY_LIMIT);
}

function sanitizeHistoryByUser(value: unknown): Record<string, ScanHistoryEntry[]> {
  if (!isRecord(value)) return {};
  const buckets: Record<string, ScanHistoryEntry[]> = {};
  for (const [username, bucket] of Object.entries(value)) {
    if (username === "") continue;
    buckets[username] = sanitizeBucket(bucket);
  }
  return buckets;
}

/**
 * The per-user, browser-local record of the last {@link SCAN_HISTORY_LIMIT}
 * scans (ADR-0041). Persisted independently from `usePreferencesStore` so no
 * catalog-adjacent or identity state can leak into the preference payload.
 */
export const useScanHistoryStore = create<ScanHistoryState>()(
  persist(
    (set, get) => ({
      historyByUser: {},
      activeUser: undefined,
      setActiveUser: (activeUser) => set({ activeUser }),
      recordScan: ({ code, format, payload }) => {
        const username = get().activeUser;
        const trimmed = code.trim();
        if (!username) {
          log.debug("[scan-history] No signed-in user; scan not recorded");
          return undefined;
        }
        if (!trimmed) return undefined;
        const at = Date.now();
        const bucket = get().historyByUser[username] ?? [];
        const newest = bucket[0];
        if (newest && newest.code === trimmed && at - newest.at <= SCAN_HISTORY_DEDUPE_MS) {
          const collapsed: ScanHistoryEntry = { ...newest, at, count: newest.count + 1 };
          set((state) => ({
            historyByUser: {
              ...state.historyByUser,
              [username]: [collapsed, ...bucket.slice(1)],
            },
          }));
          return collapsed.id;
        }
        const entry: ScanHistoryEntry = {
          id: newEntryId(),
          code: trimmed,
          at,
          format,
          payload,
          count: 1,
        };
        set((state) => ({
          historyByUser: {
            ...state.historyByUser,
            [username]: [entry, ...bucket].slice(0, SCAN_HISTORY_LIMIT),
          },
        }));
        return entry.id;
      },
      updateOutcome: (entryId, outcome) => {
        const username = get().activeUser;
        if (!username) return;
        const bucket = get().historyByUser[username] ?? [];
        if (!bucket.some((entry) => entry.id === entryId)) return;
        set((state) => ({
          historyByUser: {
            ...state.historyByUser,
            [username]: (state.historyByUser[username] ?? []).map((entry) =>
              entry.id === entryId ? { ...entry, outcome } : entry,
            ),
          },
        }));
      },
      clearHistory: () => {
        const username = get().activeUser;
        if (username) get().clearForUser(username);
      },
      clearForUser: (username) => {
        if (!username || !(username in get().historyByUser)) {
          // Nothing stored for the signing-out user: nothing to clear.
          return;
        }
        set((state) => {
          const next = { ...state.historyByUser };
          delete next[username];
          return { historyByUser: next };
        });
      },
      entries: (username) => {
        const bucket = get().historyByUser[username ?? get().activeUser ?? ""];
        return bucket ?? EMPTY_SCAN_HISTORY;
      },
    }),
    {
      name: SCAN_HISTORY_STORAGE_KEY,
      version: SCAN_HISTORY_STORAGE_VERSION,
      storage: createJSONStorage(safeStorage),
      partialize: (state) => ({ historyByUser: state.historyByUser }),
      merge: (persisted, current) => {
        const p = (persisted ?? {}) as { historyByUser?: unknown };
        return {
          ...current,
          historyByUser: sanitizeHistoryByUser(p.historyByUser),
        };
      },
    },
  ),
);

/**
 * The active user's entries, newest first — a selector-shaped alternative to
 * the store's `entries()` for components (ticket 05's overlay).
 */
export function selectScanHistoryEntries(
  state: Pick<ScanHistoryState, "historyByUser" | "activeUser">,
  username?: string,
): readonly ScanHistoryEntry[] {
  return state.historyByUser[username ?? state.activeUser ?? ""] ?? EMPTY_SCAN_HISTORY;
}
